import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';
import { TopBarRoute } from 'common/models/TopBarTypes';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import NavBarSubmenuContainer from './NavBarSubmenuContainer';

interface Props {
  module: TopBarRoute;
  moduleIndex: number;
  selectedModule: number | null;
  isSideBarOpen: boolean;
  changeSelectedModule: (selectedIndex: number | null) => void;
  showCreateOpportunityDialogHandler?: () => void;
}

const HOVER_OUT_DELAY_MS = 250;
const HOVER_ENTER_DELAY_MS = 200;

export default function SideNavBarModuleRoot({
  module,
  moduleIndex,
  selectedModule,
  isSideBarOpen,
  changeSelectedModule,
  showCreateOpportunityDialogHandler,
}: Props) {
  const [moduleAnchor, setModuleAnchor] = useState<HTMLElement | null>(null);
  const [isRootHovered, setIsRootHovered] = useState(false);
  const [startHoverOutDelay, setStartHoverOutDelay] = useState(false);
  const [startHoverEnterDelay, setStartHoverEnterDelay] = useState(false);

  useEffect(() => {
    if (startHoverOutDelay) {
      const delayHoverOut = setTimeout(() => {
        setIsRootHovered(false);
        setStartHoverOutDelay(false);
      }, HOVER_OUT_DELAY_MS);

      return () => clearTimeout(delayHoverOut);
    }
  }, [startHoverOutDelay]);

  useEffect(() => {
    if (startHoverEnterDelay) {
      const delayHoverEnter = setTimeout(() => {
        setIsRootHovered(true);
        changeSelectedModule(moduleIndex);
        setStartHoverEnterDelay(false);
      }, HOVER_ENTER_DELAY_MS);

      return () => clearTimeout(delayHoverEnter);
    }
  }, [startHoverEnterDelay, moduleIndex, changeSelectedModule]);

  const handleHover = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      if (selectedModule === null) {
        setIsRootHovered(true);
        changeSelectedModule(moduleIndex);
      } else {
        setStartHoverEnterDelay(true);
      }
      setStartHoverOutDelay(false);
      setModuleAnchor(e.currentTarget);
    },
    [changeSelectedModule, moduleIndex, selectedModule]
  );

  const onRootExit = useCallback(() => {
    // setIsRootHovered(false);
    setStartHoverOutDelay(true);
    setStartHoverEnterDelay(false);
  }, []);

  const handleCloseSubmodule = useCallback(() => {
    changeSelectedModule(null);
    setModuleAnchor(null);
  }, [changeSelectedModule]);

  const isSelected = useMemo(() => moduleIndex === selectedModule, [moduleIndex, selectedModule]);
  const showSubmenu = useMemo(
    () => isSelected && Boolean(moduleAnchor) && Boolean(module.options),
    [isSelected, moduleAnchor, module.options]
  );

  if (module.allow && !module.allow()) return <></>;

  return (
    <>
      <ListItem
        key={moduleIndex}
        disablePadding
        sx={{ display: 'block', direction: 'ltr' }}
        onMouseEnter={handleHover}
        onMouseLeave={onRootExit}
      >
        <Link
          to={module.path}
          style={{ textDecoration: 'none', color: 'inherit' }}
          onClick={handleCloseSubmodule}
        >
          <Tooltip
            title={module.text}
            placement="right"
            disableHoverListener={isSideBarOpen}
            slotProps={{
              popper: { modifiers: [{ name: 'offset', options: { offset: [-35, 0] } }] },
            }}
          >
            <ListItemButton
              sx={{
                paddingY: 0,
                px: 2.5,
                justifyContent: isSideBarOpen ? 'initial' : 'center',
                marginTop: '6px',
              }}
            >
              <ListItemIcon
                sx={{ minWidth: 0, justifyContent: 'center', mr: isSideBarOpen ? 3 : 'auto' }}
              >
                {module.icon}
              </ListItemIcon>
              <ListItemText primary={module.text} sx={{ opacity: isSideBarOpen ? 1 : 0 }} />
            </ListItemButton>
          </Tooltip>
        </Link>
      </ListItem>
      {showSubmenu && (
        <NavBarSubmenuContainer
          anchor={moduleAnchor}
          options={module.options}
          isRootHovered={isRootHovered}
          closeSubmodule={handleCloseSubmodule}
          showCreateOpportunityDialogHandler={showCreateOpportunityDialogHandler}
        />
      )}
    </>
  );
}
